/**
 * The drifting dot field behind the dark portal sign-in screens.
 *
 * Deliberately framework-free and singleton. Signing in walks a series of dark
 * full-viewport screens — loading splash, sign-in card, company picker, kiosk
 * picker, then the splash again while the portal builds — and no two of them
 * are ever mounted together. So the dots and the frame loop live here, each
 * screen attaches its own canvas, and every attached canvas is painted from the
 * same dots: swapping screens reveals a field that is mid-drift rather than
 * freshly seeded. The field goes quiet on its own once the last screen
 * detaches, which is when the portal takes over.
 *
 * The splash also paints a still first frame from an inline script, because
 * this module is a deferred Vite module and in development does not evaluate
 * for seconds. See SEED_KEY.
 *
 * Canvas rather than DOM nodes because `tw-translate-*`/`tw-scale-*` are inert
 * in this app: `@tailwind base` is commented out, so the `--tw-*` custom
 * properties those utilities resolve through are never emitted.
 *
 * The instance is stashed on `window`: the `*_login.ts` and `*_core.ts`
 * entrypoints are separate Vite bundles and do NOT share module instances, so
 * a bare module-level singleton would give a page two independent frame loops.
 */

const GLOBAL_KEY = "__portalParticleField";
/**
 * Seed published by the loading splash's inline script. It paints the first,
 * still frame at first paint — this module is a deferred Vite module and in
 * development does not evaluate for seconds — and adopting its dots verbatim is
 * what makes the stars start moving rather than being replaced.
 */
const SEED_KEY = "__portalParticleSeed";
/** Canvas the loading splash renders for the field to adopt. */
export const SPLASH_CANVAS_ID = "portal-splash-particles";

const DOT_COUNT = 110;
/** Share of dots tinted brand green; the rest are white at lower alpha. */
const GREEN_SHARE = 0.34;
/** Dots within this many CSS px of the cursor are pushed outward. */
const REPEL_RADIUS = 110;
const REPEL_STRENGTH = 1.1;
/**
 * Dots dissolve within this many px of an edge. Without it, recycling a dot
 * that has drifted off the top reads as a dot popping into existence — the
 * "jump" a continuous-looking field must not have.
 */
const EDGE_FADE_PX = 90;
/** One frame at 60Hz. Motion scales by elapsed/this, so 120Hz matches 60Hz. */
const REFERENCE_FRAME_MS = 1000 / 60;
/** Cap on a single step, so returning from a background tab doesn't teleport. */
const MAX_FRAME_MULTIPLE = 3;
/**
 * Stop drifting after this long with no input. Store tablets sit parked on the
 * sign-in card for whole shifts, and a 60Hz loop there keeps the device from
 * ever idling. Any pointer or key activity starts it again.
 */
const IDLE_TIMEOUT_MS = 3 * 60 * 1000;

interface Dot {
  x: number;
  y: number;
  radius: number;
  velocityX: number;
  velocityY: number;
  alpha: number;
  green: boolean;
}

interface ParticleSeed {
  dots: Dot[];
  width: number;
  height: number;
}

export interface ParticleField {
  /** Paints `canvas` from the shared dots. Returns a detach function. */
  attach(canvas: HTMLCanvasElement): () => void;
  /** Attaches the loading splash's canvas, if the splash is on the page. */
  attachSplash(): void;
  /** Releases it, so a hidden splash can't hold the frame loop open. */
  detachSplash(): void;
}

function readSeed(): ParticleSeed | null {
  const seed = (window as unknown as Record<string, unknown>)[SEED_KEY];
  if (!seed || typeof seed !== "object") return null;
  const candidate = seed as Partial<ParticleSeed>;
  if (!Array.isArray(candidate.dots) || candidate.dots.length === 0) return null;
  return candidate as ParticleSeed;
}

function createDot(width: number, height: number): Dot {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    radius: Math.random() * 1.5 + 0.6,
    velocityX: (Math.random() - 0.5) * 0.22,
    velocityY: -(Math.random() * 0.18 + 0.05),
    alpha: Math.random() * 0.55 + 0.2,
    green: Math.random() < GREEN_SHARE,
  };
}

/**
 * 0 at the nearer edge, 1 once EDGE_FADE_PX inside it. Exported for testing.
 *
 * Distance to the NEARER edge, not a pair of branches: on an extent shorter
 * than two fade bands the first matching branch would win and leave the other
 * edge unfaded.
 */
export function edgeFade(value: number, extent: number): number {
  const distance = Math.min(value, extent - value);
  return Math.max(0, Math.min(1, distance / EDGE_FADE_PX));
}

function createField(): ParticleField {
  const targets = new Map<HTMLCanvasElement, CanvasRenderingContext2D>();
  let dots: Dot[] = [];
  // Every host is viewport-sized (`fixed inset-0`), so one field space serves
  // them all and a resize needs no per-canvas bookkeeping.
  let width = 0;
  let height = 0;
  let pointerX = Number.NEGATIVE_INFINITY;
  let pointerY = Number.NEGATIVE_INFINITY;
  let frameHandle: number | null = null;
  let lastTimestamp: number | null = null;
  let listening = false;
  let detachSplashCanvas: (() => void) | null = null;
  let lastActivityAt = Date.now();

  // Held for the field's lifetime: matchMedia() returns a NEW MediaQueryList on
  // every call, so adding and removing through separate calls would register on
  // one object and remove from another, leaking a listener per attach/detach.
  // `.matches` on the retained object stays live.
  const motionQuery =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;

  const prefersReducedMotion = () => motionQuery?.matches === true;

  const resize = () => {
    const nextWidth = window.innerWidth;
    const nextHeight = window.innerHeight;

    if (dots.length === 0) {
      const seed = readSeed();
      if (seed) {
        // The splash seeded against its own viewport, which the operator may
        // have resized before this module evaluated. Scale into the current one
        // rather than letting dots sit outside it waiting to drift back.
        dots = seed.dots;
        width = seed.width > 0 ? seed.width : nextWidth;
        height = seed.height > 0 ? seed.height : nextHeight;
      } else {
        width = nextWidth;
        height = nextHeight;
        dots = Array.from({ length: DOT_COUNT }, () =>
          createDot(width, height),
        );
      }
    }

    if (nextWidth !== width || nextHeight !== height) {
      const scaleX = nextWidth / (width || nextWidth);
      const scaleY = nextHeight / (height || nextHeight);
      dots.forEach((dot) => {
        dot.x *= scaleX;
        dot.y *= scaleY;
      });
      width = nextWidth;
      height = nextHeight;
    }

    const ratio = window.devicePixelRatio || 1;
    const backingWidth = Math.round(width * ratio);
    const backingHeight = Math.round(height * ratio);
    targets.forEach((context, canvas) => {
      // Assigning width/height clears the bitmap even when the value is
      // unchanged, which would wipe the splash's still frame on adopt and blank
      // a visible canvas until the next rAF. Only touch it when it has moved.
      if (canvas.width === backingWidth && canvas.height === backingHeight) {
        return;
      }
      canvas.width = backingWidth;
      canvas.height = backingHeight;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    });
  };

  const advance = (delta: number) => {
    dots.forEach((dot) => {
      dot.x += dot.velocityX * delta;
      dot.y += dot.velocityY * delta;

      const offsetX = dot.x - pointerX;
      const offsetY = dot.y - pointerY;
      const distance = Math.hypot(offsetX, offsetY) || 1;
      if (distance < REPEL_RADIUS) {
        const push =
          ((REPEL_RADIUS - distance) / REPEL_RADIUS) * REPEL_STRENGTH * delta;
        dot.x += (offsetX / distance) * push;
        dot.y += (offsetY / distance) * push;
      }

      // Dots drift upward; recycle along the bottom edge at a fresh column so
      // long sessions don't wear visible columns into the field. The edge fade
      // is what makes the wrap invisible.
      if (dot.y < -EDGE_FADE_PX) {
        dot.y = height + EDGE_FADE_PX;
        dot.x = Math.random() * width;
      }
      // A dot seeded against a taller viewport starts below this one and would
      // otherwise take tens of seconds to drift into view.
      if (dot.y > height + EDGE_FADE_PX) dot.y = height + EDGE_FADE_PX;
      if (dot.x < -EDGE_FADE_PX) dot.x = width + EDGE_FADE_PX;
      if (dot.x > width + EDGE_FADE_PX) dot.x = -EDGE_FADE_PX;
    });
  };

  const paint = () => {
    targets.forEach((context, canvas) => {
      // A hidden host (the retired splash, still in the DOM as the logout
      // cover) measures 0 and is not worth painting.
      if (!canvas.isConnected || canvas.clientWidth === 0) return;
      context.clearRect(0, 0, width, height);
      dots.forEach((dot) => {
        const alpha =
          dot.alpha *
          Math.min(edgeFade(dot.y, height), edgeFade(dot.x, width));
        if (alpha <= 0) return;
        context.beginPath();
        context.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        context.fillStyle = dot.green
          ? `rgba(96, 206, 120, ${alpha})`
          : `rgba(255, 255, 255, ${alpha * 0.7})`;
        context.fill();
      });
    });
  };

  const step = (timestamp: number) => {
    // Released before any work: a throw in advance/paint would otherwise leave
    // a stale handle here, and start() would refuse to run ever again.
    frameHandle = null;

    if (Date.now() - lastActivityAt > IDLE_TIMEOUT_MS) {
      lastTimestamp = null;
      return;
    }

    const elapsed =
      lastTimestamp === null ? REFERENCE_FRAME_MS : timestamp - lastTimestamp;
    lastTimestamp = timestamp;
    advance(Math.min(elapsed / REFERENCE_FRAME_MS, MAX_FRAME_MULTIPLE));
    paint();
    frameHandle = requestAnimationFrame(step);
  };

  const stop = () => {
    if (frameHandle !== null) {
      cancelAnimationFrame(frameHandle);
      frameHandle = null;
    }
    lastTimestamp = null;
  };

  const start = () => {
    if (frameHandle !== null || targets.size === 0) return;
    if (prefersReducedMotion() || document.hidden) return;
    frameHandle = requestAnimationFrame(step);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) stop();
    else start();
  };

  const handlePointerMove = (event: MouseEvent) => {
    pointerX = event.clientX;
    pointerY = event.clientY;
    noteActivity();
  };

  // Without this the last known cursor position keeps pushing a hole in the
  // field for as long as the screen is up.
  //
  // Registered on `document` in the BUBBLE phase, which is load-bearing.
  // mouseleave doesn't bubble, but the browser does dispatch it on `document`
  // when the pointer leaves the viewport, and only then — verified in Chromium.
  // Capture is the tempting "fix" and is wrong: the capture phase also passes
  // through `document` for mouseleave targeted at descendants, so it fires
  // every time the pointer leaves any element and resets the repel origin
  // mid-page.
  const handlePointerLeave = () => {
    pointerX = Number.NEGATIVE_INFINITY;
    pointerY = Number.NEGATIVE_INFINITY;
  };

  const noteActivity = () => {
    lastActivityAt = Date.now();
    start();
  };

  // The CSS half of the entrance responds to an OS toggle live, so the field
  // should too rather than staying stuck at whatever was true on attach.
  const handleMotionPreferenceChange = () => {
    if (prefersReducedMotion()) {
      stop();
      paint();
    } else {
      start();
    }
  };

  const handleResize = () => {
    resize();
    if (prefersReducedMotion()) paint();
  };

  const listen = () => {
    if (listening) return;
    listening = true;
    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("keydown", noteActivity);
    window.addEventListener("pointerdown", noteActivity);
    document.addEventListener("mouseleave", handlePointerLeave);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    motionQuery?.addEventListener?.("change", handleMotionPreferenceChange);
  };

  const unlisten = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("mousemove", handlePointerMove);
    window.removeEventListener("keydown", noteActivity);
    window.removeEventListener("pointerdown", noteActivity);
    document.removeEventListener("mouseleave", handlePointerLeave);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    motionQuery?.removeEventListener?.("change", handleMotionPreferenceChange);
  };

  const field: ParticleField = {
    attach(canvas: HTMLCanvasElement) {
      const context = canvas.getContext("2d");
      // No 2d context (jsdom, or a browser refusing another one) is not worth
      // failing a sign-in over — the screen simply has no field.
      if (!context) return () => {};

      targets.set(canvas, context);
      resize();
      listen();
      lastActivityAt = Date.now();
      // Synchronously, so a newly attached canvas is never blank for a frame.
      paint();
      start();

      return () => {
        targets.delete(canvas);
        if (targets.size === 0) {
          stop();
          unlisten();
        }
      };
    },

    attachSplash() {
      if (detachSplashCanvas) return;
      const canvas = document.getElementById(SPLASH_CANVAS_ID);
      if (!(canvas instanceof HTMLCanvasElement)) return;
      detachSplashCanvas = field.attach(canvas);
    },

    detachSplash() {
      detachSplashCanvas?.();
      detachSplashCanvas = null;
      // The splash node is kept in the DOM to be re-shown as the logout cover,
      // and `display: none` does not release a canvas bitmap — a viewport-sized
      // one at devicePixelRatio 2 is tens of MB parked for the whole session.
      // resize() reallocates it if the cover ever comes back.
      const canvas = document.getElementById(SPLASH_CANVAS_ID);
      if (canvas instanceof HTMLCanvasElement) {
        canvas.width = 0;
        canvas.height = 0;
      }
    },
  };

  return field;
}

/** The page's one field, created on first use and shared across bundles. */
export function getParticleField(): ParticleField {
  const host = window as unknown as Record<string, ParticleField | undefined>;
  const existing = host[GLOBAL_KEY];
  if (existing) return existing;
  const field = createField();
  host[GLOBAL_KEY] = field;
  return field;
}

/**
 * Starts the field on the loading splash, so it is already drifting by the time
 * the sign-in card mounts over it. A no-op when the splash isn't on the page.
 */
export function attachSplashParticleField(): void {
  getParticleField().attachSplash();
}

/**
 * Releases the splash canvas. Called wherever the splash is hidden: it stays in
 * the DOM to be re-shown as the logout cover, so left attached it would be the
 * one target keeping the frame loop alive for the whole of a live portal
 * session, painting nothing.
 */
export function detachSplashParticleField(): void {
  getParticleField().detachSplash();
}
